import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Input, Table } from 'antd';
import { useNavigate } from 'react-router';

import { useGetSchoolsDataQuery } from '@/api/endpoints/schools';
import { FetchDataWrapper } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { debounce } from '@/shared/helpers';
import { usePageTitle } from '@/shared/hooks';
import type { ISchoolShortData } from '@/shared/types';

import type { IUnpublishModalSchoolInfo } from './_components/UnpublishSchoolModal/types';
import { UnpublishSchoolModal } from './_components/UnpublishSchoolModal/UnpublishSchoolModal';
import { getSchoolTableColumns } from './helpers';
import styles from './SchoolsCatalogPage.module.scss';

export const SchoolsCatalogPage = () => {
  usePageTitle('Школы');

  const navigate = useNavigate();

  const {
    data: schoolsData,
    isLoading: isSchoolsDataLoading,
    isError: isSchoolDataFetchError,
  } = useGetSchoolsDataQuery();

  const [currentSchoolsData, setCurrentSchoolsData] = useState<ISchoolShortData[] | []>([]);
  const [isUnpublishSchoolModalOpened, setIsUnpublishSchoolModalOpened] = useState(false);
  const [currentSchoolInfo, setCurrentSchoolInfo] = useState<IUnpublishModalSchoolInfo | null>();

  const openUnpublishSchoolModal = useCallback(() => setIsUnpublishSchoolModalOpened(true), []);
  const closeUnpublishSchoolModal = useCallback(() => setIsUnpublishSchoolModalOpened(false), []);

  useEffect(() => {
    if (schoolsData) {
      setCurrentSchoolsData(schoolsData);
    }
  }, [schoolsData]);

  const onSearchHandler = debounce({
    callback: (e: ChangeEvent<HTMLInputElement>) => {
      if (!schoolsData) {
        return;
      }

      const filteredData = schoolsData?.filter((school) =>
        school.name.toLowerCase().includes(e.target.value.trim().toLowerCase().replace(/\s+/g, ' ')),
      );

      setCurrentSchoolsData(filteredData);
    },
  });

  const handleWithClearCurrentSchool = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setCurrentSchoolInfo(null);
  };

  return (
    <FetchDataWrapper
      isLoading={isSchoolsDataLoading}
      isError={isSchoolDataFetchError}
    >
      {schoolsData && (
        <div className={styles.wrapper}>
          <Breadcrumb items={[{ title: 'Публичные страницы' }, { title: 'Школы' }]} />
          <div className={styles['search-and-button-wrapper']}>
            <Input
              type="search"
              prefix={<SearchOutlined />}
              onChange={onSearchHandler}
              placeholder="Поиск"
              className={styles['search-input']}
            />
            <Button
              type="primary"
              onClick={() => navigate(ROUTES.CREATE)}
            >
              Создать школу
            </Button>
          </div>
          <Table
            dataSource={currentSchoolsData}
            columns={getSchoolTableColumns({
              openUnpublishModalCb: openUnpublishSchoolModal,
              setCurrentSchoolInfoCb: setCurrentSchoolInfo,
            })}
            rowKey="id"
            onRow={(record) => ({
              style: { cursor: 'pointer' },
              onClick: () => void navigate(`${record.id}/${record.active ? ROUTES.VIEW : ROUTES.EDIT}`),
            })}
            size="large"
            className={styles.table}
            locale={TABLE_TRANSLATIONS}
            pagination={false}
          />
          <UnpublishSchoolModal
            isModalOpen={isUnpublishSchoolModalOpened}
            handleCancel={() => handleWithClearCurrentSchool(closeUnpublishSchoolModal)}
            schoolInfo={currentSchoolInfo}
          />
        </div>
      )}
    </FetchDataWrapper>
  );
};
